import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Box, Divider, IconButton, Toolbar, Tooltip } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";
import { type FunctionComponent, type ReactNode } from "react";
import { SIDEBAR_EXPANDED_WIDTH } from "../constants";

// Reference: https://github.com/mui/material-ui/tree/v5.14.0/docs/data/material/getting-started/templates/dashboard
const Drawer = styled(MuiDrawer)(() => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: SIDEBAR_EXPANDED_WIDTH,
    boxSizing: "border-box",
    borderRight: "none",
  },
}));

interface SidebarProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  children: ReactNode;
}

const Sidebar: FunctionComponent<SidebarProps> = ({
  isSidebarOpen,
  onToggleSidebar,
  children,
}) => {
  return (
    /*
     * The collapsing is done by this wrapper rather than by the Drawer itself.
     * A docked Drawer resists being narrowed from the inside: its paper is
     * stretched to fill the container, and the container's own width loses to
     * MUI's internals. Clipping a full-width drawer inside a box whose width
     * we own is both simpler and reliable.
     *
     * Collapsed it goes to nothing at all: the canvas is what the app is for,
     * and every pixel the column keeps is one the graph doesn't get. The way
     * back is the menu icon in the title bar, which appears exactly when this
     * column is gone.
     */
    <Box
      data-testid="sidebar"
      sx={{
        // This box is a flex item, so its main size comes from `flex-basis`.
        // Setting it explicitly next to `width` states the intent instead of
        // relying on `flex-basis: auto` resolving back to `width`
        width: isSidebarOpen ? `${SIDEBAR_EXPANDED_WIDTH}px` : "0px",
        flexBasis: isSidebarOpen ? `${SIDEBAR_EXPANDED_WIDTH}px` : "0px",
        flexGrow: 0,
        flexShrink: 0,
        // Flex items default to `min-width: auto`, which would clamp the box to
        // the width of the nav list inside it
        minWidth: 0,
        overflow: "hidden",
        borderRight: isSidebarOpen ? 1 : 0,
        borderColor: "divider",
        transition: (theme) =>
          theme.transitions.create(["flex-basis", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: isSidebarOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      <Drawer variant="permanent">
        {/* Spacer that pushes the navigator clear of the title bar. There is
        nothing to click here once the column is collapsed to nothing, so the
        button only exists while it is open */}
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: [1],
          }}
        >
          {isSidebarOpen && (
            <Tooltip title="Hide the left column">
              <IconButton
                aria-label="Toggle Sidebar"
                onClick={() => {
                  onToggleSidebar();
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
        <Divider />
        {children}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
